import {
  ITrackingEvent,
  TrackingEventState,
  DeliveryErrorCode,
} from "@swyft/swyft-common";
import { IconType } from "react-icons";
import { FaHistory } from "react-icons/fa";
import { StaticImageData } from "next/image";
import LabelIcon from "~/images/helo-label.png";
import WarehouseIcon from "~/images/helo-warehouse.png";
import DeliveryIcon from "~/images/helo-delivery.png";
import DeliveryFailed from "~/images/shipment-error.png";
import DeliverySuccessIcon from "~/images/helo-delivery-success.png";

type StateMap = {
  [key in TrackingEventState]: StateMapData;
};

type BuildSteps = (
  events: ITrackingEvent[],
  merchant: IMerchantDetails,
) => IStepData[];

export interface IMerchantDetails {
  name: string;
}

interface StateMapData {
  title: string;
  details?: string;
  icon: StaticImageData | IconType;
  failed?: boolean;
  failures?: {
    [key in DeliveryErrorCode]?: Partial<
      Omit<StateMapData, "failed" | "failures">
    >;
  };
}

export interface IStepData
  extends Omit<ITrackingEvent, "source" | "details" | "timestamp">,
    StateMapData {
  details?: string;
  /** Timestamp when event performed, If timestamp is null event is not performed yet */
  timestamp: string | Date | null;
}

export const statesDataMap: StateMap = {
  PENDING: {
    title: "Label Created",
    details: "We are on our way to pick up your package!",
    icon: LabelIcon,
  },
  RECEIVED: {
    title: "Scanned at Warehouse",
    details: "We have received your package at a local sort center.",
    icon: WarehouseIcon,
  },
  IN_TRANSIT: {
    title: "Out for Delivery",
    details: "The package is out for delivery.",
    icon: DeliveryIcon,
  },
  DELIVERED: {
    title: "Delivered",
    details: "Your package has been delivered.",
    icon: DeliverySuccessIcon,
  },
  FAILED: {
    title: "Delivery Failed",
    details: "Your package could not be delivered",
    icon: DeliveryFailed,
    failed: true,
    failures: {
      NO_ACCESS_COMMERCIAL_ADDRESS: {
        details:
          "We could not access your building. We will reattempt your delivery within 2 business days. Please contact customer support for further details.",
      },
      NO_ACCESS_RESIDENTIAL_ADDRESS: {
        details:
          "We could not access your building. We will reattempt your delivery within 2 business days. Please contact customer support for further details.",
      },
      UNABLE_TO_COLLECT_SIGNATURE: {
        details:
          "We could not collect a signature for your package. We will reattempt your delivery within 2 business days. Please contact customer support for further details.",
      },
      CUSTOMER_REFUSED_DELIVERY: {
        details:
          "Recipient refused delivery. Please contact customer support for further details.",
      },
      INCORRECT_CUSTOMER_ADDRESS: {
        details:
          "The address provided on the label is incorrect. We will reattempt your delivery within 2 business days. Please contact customer support for further details.",
      },
      ITEM_DAMAGED: {
        details:
          "The package will be returned to sender due to damage. Please contact customer support for further details.",
      },
      MISSING_PACKAGE: {
        title: "Delayed",
        icon: FaHistory,
        details:
          "Sorry, we had an issue with your delivery. We will reattempt your delivery within 2 business days.",
      },
      DRIVER_ACCIDENT: {
        title: "Delayed",
        icon: FaHistory,
        details:
          "Sorry, we had an issue with your delivery. We will reattempt your delivery within 2 business days.",
      },
      PAST_DELIVERY_WINDOW: {
        title: "Delayed",
        icon: FaHistory,
        details:
          "Sorry, we had an issue with your delivery. We will reattempt your delivery within 2 business days.",
      },
      NO_SPACE_IN_CAR: {
        title: "Delayed",
        icon: FaHistory,
        details:
          "Sorry, we had an issue with your delivery. We will reattempt your delivery within 2 business days.",
      },
    },
  },
  DELAYED: {
    title: "Delayed",
    icon: FaHistory,
    details:
      "Sorry, we had an issue with your delivery. We will reattempt your delivery within 2 business days.",
    failed: true,
  },
  RETURNING_TO_SENDER: {
    title: "Returning to Sender",
    failed: true,
    details:
      "We were unable to deliver your package. The package will be returned. Please contact customer support for more details",
    icon: DeliveryFailed,
  },
  DELETED: {
    title: "Deleted",
    icon: DeliveryIcon,
  },
  PROBLEM: {
    title: "Problem in Delivery",
    icon: DeliveryFailed,
    details:  "Sorry, your package had an issue. Please contact customer support for further details."
  },
  CANCELED: {
    title: "Canceled",
    icon: DeliveryIcon,
  },
};

const SUMMARY_STATES = [
  TrackingEventState.PENDING,
  TrackingEventState.RECEIVED,
  TrackingEventState.IN_TRANSIT,
  TrackingEventState.DELIVERED,
];

const SUMMARY_LAST_STEP_OPTIONS = [
  TrackingEventState.DELIVERED,
  TrackingEventState.RETURNING_TO_SENDER,
  TrackingEventState.PROBLEM,
  TrackingEventState.DELAYED,
  TrackingEventState.FAILED,
  TrackingEventState.CANCELED,
];

const SUMMARY: IStepData[] = SUMMARY_STATES.map((state) => {
  return {
    state,
    timestamp: null,
    ...statesDataMap[state],
  };
});

/** Adds Merchant Name ({merchantName}) in Step Details */
const customize = (step: IStepData, merchant: IMerchantDetails) => {
  return {
    ...step,
    details: step.details.replace(/{merchantName}/g, merchant.name),
  };
};

/** Returns data required for Step component */
const getStepData = (
  event: ITrackingEvent,
  merchant: IMerchantDetails,
): IStepData => {
  const { state, details, timestamp, failureCode } = event;

  const stateData = statesDataMap[state];
  const createdStateData = { ...stateData };

  /** If given step Have failure code, update the Title and Details according to that */
  if (failureCode && stateData.failures && stateData.failures[failureCode]) {
    const failureCodeData = stateData.failures[failureCode];

    if (typeof failureCodeData === "string") {
      createdStateData.details = failureCodeData;
    } else {
      Object.assign(createdStateData, failureCodeData);
    }
  }

  return customize(
    {
      timestamp: (typeof timestamp === "string"
        ? new Date(timestamp)
        : timestamp) as Date,
      state,
      details,
      ...createdStateData,
    },
    merchant,
  );
};

/** For the summary, we need to use data from max delivery attempts */
const getSummaryEvents = (events: ITrackingEvent[]): ITrackingEvent[] => {
  if (events.length <= 4) return events;

  let firstAttemptStartIndex: number = null;
  let lastAttemptStartIndex: number = null;

  events.forEach((event, index) => {
    /** Initial State for new Delivery attempt is RECEIVED */
    if (event.state !== TrackingEventState.RECEIVED) return;

    if (firstAttemptStartIndex === null) {
      firstAttemptStartIndex = index;
    }
    lastAttemptStartIndex = index;
  });

  if (firstAttemptStartIndex === lastAttemptStartIndex) return events;

  const tempEvents = [...events];

  tempEvents.splice(
    firstAttemptStartIndex,
    lastAttemptStartIndex - firstAttemptStartIndex,
  );
  return tempEvents;
};

/**
 * Returns Steps summary for Shipping Label
 * @Note : We assume here that delivery events will always start from PENDING -> RECEIVED -> IN_TRANSIT
 */
export const buildSummary: BuildSteps = (events, merchant) => {
  const descSortedSummaryEvents = events.sort((event1, event2) => {
    return new Date(event1.timestamp as string) >
      new Date(event2.timestamp as string)
      ? -1
      : 1;
  });

  const latestSortedSummaryEvent = descSortedSummaryEvents[0];

  return SUMMARY.map((event) => {
    // only display in TRANSIT for IN_TRANSIT, FAILED or DELIVERED current state
    if (event.state === TrackingEventState.IN_TRANSIT) {
      const step = descSortedSummaryEvents.find(
        (_event) => _event.state === event.state,
      );
      return step &&
        (latestSortedSummaryEvent.state === TrackingEventState.IN_TRANSIT ||
          latestSortedSummaryEvent.state === TrackingEventState.FAILED ||
          latestSortedSummaryEvent.state === TrackingEventState.DELIVERED)
        ? getStepData(step, merchant)
        : customize(event, merchant);
    }

    /** For PENDING, RECEIVED and IN_TRANSIT event find corresponding event in Events History */
    if (!SUMMARY_LAST_STEP_OPTIONS.includes(event.state)) {
      const step = descSortedSummaryEvents.find(
        (_event) => _event.state === event.state,
      );
      return step ? getStepData(step, merchant) : customize(event, merchant);
    }

    /**
     * For the last step check if Item is RETURNED_TO_SENDER, DELIVERED or FAILED
     * @NOTE : The priority if the state is RETURNED_TO_SENDER > FAILED > DELIVERED.
     * */

    if (SUMMARY_LAST_STEP_OPTIONS.includes(latestSortedSummaryEvent.state)) {
      return getStepData(latestSortedSummaryEvent as ITrackingEvent, merchant);
    }

    return customize(event, merchant);
  });
};

// /**
//  * Returns complete steps History for a Shipping Label
//  * @Note: If step history has less than or equals to 4 events, we simply return steps summary
//  */
export const buildSteps: BuildSteps = (events, merchant) => {
  return events.map((event) => getStepData(event, merchant));
};

/**
 * Returns the Max. or Current number of the attempt
 * @param events
 */
export const getCurrentAttempt = (events: ITrackingEvent[]): number => {
  /** Initial State for new Delivery attempt is RECEIVED */
  return events.filter((event) => event.state === TrackingEventState.RECEIVED)
    .length;
};
